import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Input, Row, Col } from 'antd';
import { getAllowedEdgeTypes } from './config/edgeTypeConfig';

// 边类型定义
const edgeTypes = [
  // {
  //   id: 'triangle-filled',
  //   name: '实心三角形',
  //   color: '#ff4d4f',
  //   markerSvg:
  //     '<polygon points="0,-6 12,0 0,6" fill="#ff4d4f" stroke="#ff4d4f"/>',
  // },
  // {
  //   id: 'triangle-hollow',
  //   name: '空心三角形',
  //   color: '#1890ff',
  //   markerSvg:
  //     '<polygon points="0,-6 12,0 0,6" fill="white" stroke="#1890ff" stroke-width="2"/>',
  // },
  // {
  //   id: 'circle-filled',
  //   name: '实心圆形',
  //   color: '#52c41a',
  //   markerSvg: '<circle r="5" fill="#52c41a" stroke="#52c41a"/>',
  // },
  // {
  //   id: 'circle-hollow',
  //   name: '空心圆形',
  //   color: '#722ed1',
  //   markerSvg: '<circle r="5" fill="white" stroke="#722ed1" stroke-width="2"/>',
  // },
  // {
  //   id: 'diamond-filled',
  //   name: '实心棱形',
  //   color: '#fa8c16',
  //   markerSvg:
  //     '<polygon points="0,-5 8,0 0,5 -8,0" fill="#fa8c16" stroke="#fa8c16"/>',
  // },
  // {
  //   id: 'diamond-hollow',
  //   name: '空心棱形',
  //   color: '#eb2f96',
  //   markerSvg:
  //     '<polygon points="0,-5 8,0 0,5 -8,0" fill="white" stroke="#eb2f96" stroke-width="2"/>',
  // },
  {
    id: 'middle-arrow',
    name: '聚合关系',
    color: '#586D8C',
    markerSvg:
      '<polygon points="0,-8 15,0 0,8" fill="#586D8C" stroke="#586D8C"/>',
  },
  {
    id: 'middle-arrow-double-triangle',
    name: '展示关系',
    color: '#586D8C',
    markerSvg:
      '<polygon points="0,-8 15,0 0,8" fill="white" stroke="#586D8C" stroke-width="3"/><polygon points="4,-2 8,0 4,2" fill="#586D8C" stroke="#586D8C"/>',
  },
  {
    id: 'middle-arrow-hollow-triangle',
    name: '泛化关系',
    color: '#586D8C',
    markerSvg:
      '<polygon points="0,-8 15,0 0,8" fill="white" stroke="#586D8C" stroke-width="3"/>',
  },
  {
    id: 'middle-arrow-triangle-circle',
    name: '实例化关系',
    color: '#586D8C',
    markerSvg:
      '<polygon points="0,-8 15,0 0,8" fill="white" stroke="#586D8C" stroke-width="3"/><circle cx="6" cy="0" r="2.5" fill="#586D8C" stroke="#586D8C"/>',
  },
  {
    id: 'polyline',
    name: '单向关联关系',
    color: '#666',
    markerSvg: '<polygon points="0,-4 8,0 0,4" fill="#666" stroke="#666"/>',
  },
  {
    id: 'bidirectional-arrow',
    name: '对等关联关系',
    color: '#333333',
    markerSvg:
      '<polygon points="0,-4 8,0 0,4" fill="#333333" stroke="#333333"/><polygon points="72,-4 64,0 72,4" fill="#333333" stroke="#333333"/>',
  },
];

interface EdgeTypeDialogProps {
  visible: boolean;
  sourceNode?: string;
  targetNode?: string;
  sourceNodeType?: string;  // 源节点类型
  targetNodeType?: string;  // 目标节点类型
  currentEdgeType?: string;
  currentEdgeText?: string;
  isEditing?: boolean;
  onConfirm: (config: { edgeType: string; edgeText: string }) => void;
  onCancel: () => void;
}

const EdgeTypeDialog: React.FC<EdgeTypeDialogProps> = ({
  visible,
  sourceNode = '',
  targetNode = '',
  sourceNodeType = '',
  targetNodeType = '',
  currentEdgeType = '',
  currentEdgeText = '',
  isEditing = false,
  onConfirm,
  onCancel,
}) => {
  const [selectedType, setSelectedType] = useState<string>('');
  const [edgeText, setEdgeText] = useState<string>('');

  // 根据节点类型过滤可用的边类型
  const availableEdgeTypes = useMemo(() => {
    // 如果没有提供节点类型信息，返回所有类型
    if (!sourceNodeType || !targetNodeType) {
      return edgeTypes;
    }

    // 获取允许的边类型ID列表
    const allowedTypeIds = getAllowedEdgeTypes(sourceNodeType, targetNodeType);
    
    // 过滤边类型配置，只保留被允许的类型
    return edgeTypes.filter(edgeType => allowedTypeIds.includes(edgeType.id));
  }, [sourceNodeType, targetNodeType]);

  // 监听弹窗显示状态，设置预选中的边类型
  useEffect(() => {
    if (visible && isEditing) {
      // 编辑模式：设置当前值
      if (currentEdgeType) {
        // 映射边类型到弹窗中的类型ID
        const typeMapping: Record<string, string> = {
          polyline: 'polyline',
          'triangle-filled': 'triangle-filled',
          'triangle-hollow': 'triangle-hollow',
          'circle-filled': 'circle-filled',
          'circle-hollow': 'circle-hollow',
          'diamond-filled': 'diamond-filled',
          'diamond-hollow': 'diamond-hollow',
          'middle-arrow': 'middle-arrow',
          'middle-arrow-double-triangle': 'middle-arrow-double-triangle',
          'middle-arrow-hollow-triangle': 'middle-arrow-hollow-triangle',
          'middle-arrow-triangle-circle': 'middle-arrow-triangle-circle',
          'bidirectional-arrow': 'bidirectional-arrow',
        };

        const mappedType = typeMapping[currentEdgeType] || currentEdgeType;
        
        // 检查映射后的类型是否在可用类型中
        const isTypeAvailable = availableEdgeTypes.some(edgeType => edgeType.id === mappedType);
        if (isTypeAvailable) {
          setSelectedType(mappedType);
          console.log('预选中边类型:', currentEdgeType, '->', mappedType);
        } else {
          // 如果当前类型不可用，选择第一个可用类型
          const firstAvailableType = availableEdgeTypes[0]?.id || '';
          setSelectedType(firstAvailableType);
          console.log('当前边类型不可用，选择默认类型:', firstAvailableType);
        }
      }

      // 设置边文字
      setEdgeText(currentEdgeText || '');
    } else if (visible && !isEditing) {
      // 新建边时设置默认值 - 选择第一个可用类型
      const defaultType = availableEdgeTypes[0]?.id || '';
      setSelectedType(defaultType);
      setEdgeText('');
    }
  }, [visible, isEditing, currentEdgeType, currentEdgeText, availableEdgeTypes]);

  // 选择边类型
  const selectType = (typeId: string) => {
    setSelectedType(typeId);
  };

  // 确认选择
  const handleConfirm = () => {
    if (selectedType) {
      onConfirm({
        edgeType: selectedType,
        edgeText: edgeText.trim(),
      });
      // 重置状态
      setSelectedType('');
      setEdgeText('');
    }
  };

  // 取消选择
  const handleCancel = () => {
    onCancel();
    setSelectedType('');
    setEdgeText('');
  };

  return (
    <Modal
      title={isEditing ? '修改连接线类型' : '选择连接线类型'}
      open={visible}
      onOk={handleConfirm}
      onCancel={handleCancel}
      width={600}
      okText="确认"
      cancelText="取消"
      okButtonProps={{ disabled: !selectedType }}
    >
      <div className="space-y-0">
        {/* 边箭头类型设置 */}
        <div>
          {/* <label className="block text-sm font-medium text-gray-700 mb-2">
            箭头类型：
          </label> */}
          <Row gutter={[12, 12]}>
            {availableEdgeTypes.map(edgeType => (
              <Col span={8} key={edgeType.id}>
                <div
                  className={`border-2 rounded-lg p-3 cursor-pointer transition-all text-center ${
                    selectedType === edgeType.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => selectType(edgeType.id)}
                >
                  <div className="mb-2 flex justify-center">
                    <svg width="80" height="40" viewBox="0 0 80 40">
                      {/* 连接线 */}
                      <line
                        x1="10"
                        y1="20"
                        x2="60"
                        y2="20"
                        stroke={edgeType.color}
                        strokeWidth="2"
                      />
                      {/* 正常箭头（右端） */}
                      <g
                        transform={
                          edgeType.id.startsWith('middle-arrow')
                            ? 'translate(35, 20)'
                            : 'translate(60, 20)'
                        }
                        dangerouslySetInnerHTML={{ __html: edgeType.markerSvg }}
                      />
                      {/* 双向箭头额外的左端箭头 */}
                      {edgeType.id === 'bidirectional-arrow' && (
                        <g transform="translate(10, 20)">
                          <polygon
                            points="0,-4 -8,0 0,4"
                            fill={edgeType.color}
                            stroke={edgeType.color}
                          />
                        </g>
                      )}
                    </svg>
                  </div>
                  <div
                    className={`text-xs font-medium ${
                      selectedType === edgeType.id
                        ? 'text-blue-600'
                        : 'text-gray-600'
                    }`}
                  >
                    {edgeType.name}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* 边文字设置 - 只在选择单向关联关系或对等关联关系时显示 */}
        {(selectedType === 'polyline' || selectedType === 'bidirectional-arrow') && (
          <div className="p-8">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag：
            </label>
            <Input
              value={edgeText}
              onChange={e => setEdgeText(e.target.value)}
              placeholder="请输入Tag（可选）"
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EdgeTypeDialog;
export { edgeTypes };
